import { useState, type CSSProperties } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, EditableSlotGroup } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

// ============================================================================
// Types & helpers
// ============================================================================

interface SlideData {
  image?: string | null;
  alt?: string | null;
  title?: string | null;
}

function altOf(slide: SlideData): string {
  return slide.alt ?? slide.title ?? 'Property photo';
}

// Each slide selects one gallery photo. The generator is applied to every
// property on the theme, so it states the one hard constraint — no empty rooms —
// and leaves the rest to the model. Definitions are inlined as literals (not
// built by a helper) so the preview system can statically evaluate the schema.
const slide1Def: SlotGroupDefinition = {
  label: 'Slide 1',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[0].alt_text' },
    title: { label: 'Caption', type: 'text', defaultField: 'photo_gallery[0].title' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text', title: 'title' },
  },
  generator: {
    prompt:
      'Choose a photo from the property photo gallery for this slide. Skip any photo of an empty or unfurnished room — a vacant interior with no furniture. Prefer furnished interiors, amenity spaces, exteriors, or lifestyle shots that have a real image.',
    context:
      'One of six photos in the home-page image slider — each slide must be a different gallery photo, and none should show an empty room.',
    sourceCollection: 'photo_gallery',
  },
};

const slide2Def: SlotGroupDefinition = {
  label: 'Slide 2',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[1].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[1].alt_text' },
    title: { label: 'Caption', type: 'text', defaultField: 'photo_gallery[1].title' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 1,
    fieldMappings: { image: 'image', alt: 'alt_text', title: 'title' },
  },
  generator: {
    prompt:
      'Choose a photo from the property photo gallery for this slide. Skip any photo of an empty or unfurnished room — a vacant interior with no furniture. Prefer furnished interiors, amenity spaces, exteriors, or lifestyle shots that have a real image.',
    context:
      'One of six photos in the home-page image slider — each slide must be a different gallery photo, and none should show an empty room.',
    sourceCollection: 'photo_gallery',
  },
};

const slide3Def: SlotGroupDefinition = {
  label: 'Slide 3',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[2].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[2].alt_text' },
    title: { label: 'Caption', type: 'text', defaultField: 'photo_gallery[2].title' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 2,
    fieldMappings: { image: 'image', alt: 'alt_text', title: 'title' },
  },
  generator: {
    prompt:
      'Choose a photo from the property photo gallery for this slide. Skip any photo of an empty or unfurnished room — a vacant interior with no furniture. Prefer furnished interiors, amenity spaces, exteriors, or lifestyle shots that have a real image.',
    context:
      'One of six photos in the home-page image slider — each slide must be a different gallery photo, and none should show an empty room.',
    sourceCollection: 'photo_gallery',
  },
};

const slide4Def: SlotGroupDefinition = {
  label: 'Slide 4',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[3].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[3].alt_text' },
    title: { label: 'Caption', type: 'text', defaultField: 'photo_gallery[3].title' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 3,
    fieldMappings: { image: 'image', alt: 'alt_text', title: 'title' },
  },
  generator: {
    prompt:
      'Choose a photo from the property photo gallery for this slide. Skip any photo of an empty or unfurnished room — a vacant interior with no furniture. Prefer furnished interiors, amenity spaces, exteriors, or lifestyle shots that have a real image.',
    context:
      'One of six photos in the home-page image slider — each slide must be a different gallery photo, and none should show an empty room.',
    sourceCollection: 'photo_gallery',
  },
};

const slide5Def: SlotGroupDefinition = {
  label: 'Slide 5',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[4].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[4].alt_text' },
    title: { label: 'Caption', type: 'text', defaultField: 'photo_gallery[4].title' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 4,
    fieldMappings: { image: 'image', alt: 'alt_text', title: 'title' },
  },
  generator: {
    prompt:
      'Choose a photo from the property photo gallery for this slide. Skip any photo of an empty or unfurnished room — a vacant interior with no furniture. Prefer furnished interiors, amenity spaces, exteriors, or lifestyle shots that have a real image.',
    context:
      'One of six photos in the home-page image slider — each slide must be a different gallery photo, and none should show an empty room.',
    sourceCollection: 'photo_gallery',
  },
};

const slide6Def: SlotGroupDefinition = {
  label: 'Slide 6',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[5].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[5].alt_text' },
    title: { label: 'Caption', type: 'text', defaultField: 'photo_gallery[5].title' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 5,
    fieldMappings: { image: 'image', alt: 'alt_text', title: 'title' },
  },
  generator: {
    prompt:
      'Choose a photo from the property photo gallery for this slide. Skip any photo of an empty or unfurnished room — a vacant interior with no furniture. Prefer furnished interiors, amenity spaces, exteriors, or lifestyle shots that have a real image.',
    context:
      'One of six photos in the home-page image slider — each slide must be a different gallery photo, and none should show an empty room.',
    sourceCollection: 'photo_gallery',
  },
};

const SLIDE_CLASS =
  'absolute left-1/2 top-1/2 h-full w-[92%] overflow-hidden shadow-2xl transition-all duration-500 ease-out will-change-transform motion-reduce:transition-none md:h-[92%] md:w-[64%]';

/** Outlined chevron used by the prev / next arrow buttons. */
function Chevron({ direction }: { direction: 'left' | 'right' }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5 md:h-6 md:w-6"
      aria-hidden="true"
    >
      {direction === 'left' ? (
        <polyline points="15 6 9 12 15 18" />
      ) : (
        <polyline points="9 6 15 12 9 18" />
      )}
    </svg>
  );
}

// ============================================================================
// Component
// ============================================================================

export function PhotoSlider() {
  const slide1 = useSlotGroup('photoslider.slide_1', slide1Def);
  const slide2 = useSlotGroup('photoslider.slide_2', slide2Def);
  const slide3 = useSlotGroup('photoslider.slide_3', slide3Def);
  const slide4 = useSlotGroup('photoslider.slide_4', slide4Def);
  const slide5 = useSlotGroup('photoslider.slide_5', slide5Def);
  const slide6 = useSlotGroup('photoslider.slide_6', slide6Def);

  const slides: SlideData[] = [slide1, slide2, slide3, slide4, slide5, slide6];
  const count = slides.length;

  const [active, setActive] = useState(0);
  const go = (dir: 1 | -1) => setActive((prev) => (prev + dir + count) % count);

  const renderImage = (slide: SlideData) => {
    const src = slide.image ?? '';
    if (!src) {
      return <div className="h-full w-full bg-[var(--vibe-color-surface)]" />;
    }
    return (
      <VibesitesImage
        url={src}
        alt={altOf(slide)}
        sizes="(min-width: 768px) 64vw, 92vw"
        className="h-full w-full object-cover object-center"
      />
    );
  };

  // Signed distance from the active slide, taking the shortest way around the
  // loop so wrapping (last → first) animates one step instead of rewinding, then
  // the coverflow style that eases each slide into place by that offset — the
  // active one scales up, neighbours shrink + dim, everything further out fades.
  const slideStyle = (i: number): CSSProperties => {
    let offset = i - active;
    if (offset > count / 2) offset -= count;
    if (offset < -count / 2) offset += count;
    const isActive = offset === 0;
    const isVisible = Math.abs(offset) <= 1;
    return {
      transform: `translate(-50%, -50%) translateX(${offset * 60}%) scale(${isActive ? 1 : 0.8})`,
      opacity: isVisible ? (isActive ? 1 : 0.55) : 0,
      zIndex: 20 - Math.abs(offset),
      pointerEvents: isActive ? 'auto' : 'none',
    };
  };

  return (
    <section className="relative overflow-hidden bg-[var(--vibe-color-background)] py-16 md:py-24">
      <div className="relative mx-auto h-[300px] max-w-6xl px-4 sm:h-[400px] md:h-[560px] md:px-6">
        {/* Coverflow track: every slide is centre-anchored and eased into place
            by its offset from the active slide. */}
        <EditableSlotGroup groupPath="photoslider.slide_1" definition={slide1Def} className={SLIDE_CLASS} style={slideStyle(0)}>
          {renderImage(slide1)}
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="photoslider.slide_2" definition={slide2Def} className={SLIDE_CLASS} style={slideStyle(1)}>
          {renderImage(slide2)}
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="photoslider.slide_3" definition={slide3Def} className={SLIDE_CLASS} style={slideStyle(2)}>
          {renderImage(slide3)}
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="photoslider.slide_4" definition={slide4Def} className={SLIDE_CLASS} style={slideStyle(3)}>
          {renderImage(slide4)}
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="photoslider.slide_5" definition={slide5Def} className={SLIDE_CLASS} style={slideStyle(4)}>
          {renderImage(slide5)}
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="photoslider.slide_6" definition={slide6Def} className={SLIDE_CLASS} style={slideStyle(5)}>
          {renderImage(slide6)}
        </EditableSlotGroup>

        {/* Arrows — sit on the left / right edges of the centre image */}
        <button
          type="button"
          aria-label="Previous photo"
          onClick={() => go(-1)}
          className="absolute left-2 top-1/2 z-30 flex h-11 w-11 -translate-y-1/2 items-center justify-center border-2 border-[var(--vibe-color-primary)] bg-transparent text-[var(--vibe-color-primary)] shadow-md md:left-[10%] md:h-14 md:w-14"
        >
          <Chevron direction="left" />
        </button>
        <button
          type="button"
          aria-label="Next photo"
          onClick={() => go(1)}
          className="absolute right-2 top-1/2 z-30 flex h-11 w-11 -translate-y-1/2 items-center justify-center border-2 border-[var(--vibe-color-primary)] bg-transparent text-[var(--vibe-color-primary)] shadow-md md:right-[10%] md:h-14 md:w-14"
        >
          <Chevron direction="right" />
        </button>
      </div>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'PhotoSlider',
  displayName: 'Photo Slider',
  tokens: {},
  slotGroups: {
    'photoslider.slide_1': slide1Def,
    'photoslider.slide_2': slide2Def,
    'photoslider.slide_3': slide3Def,
    'photoslider.slide_4': slide4Def,
    'photoslider.slide_5': slide5Def,
    'photoslider.slide_6': slide6Def,
  },
};

(PhotoSlider as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PhotoSlider;
